import React, { useEffect, useState, useMemo, useRef } from "react";
import DataTable from "react-data-table-component";
import adminService from "../../services/adminService";
import {
  AiOutlineEdit,
  AiOutlineDelete,
  AiOutlineSync,
  AiOutlineCheckCircle,
  AiOutlineClose,
  AiOutlineSearch,
  AiOutlinePlus,
  AiOutlineCloudDownload,
  AiOutlineFileWord,
} from "react-icons/ai";

import { API_CONFIG } from '../../config/env';
const API_BASE_URL = API_CONFIG.BASE_URL;

// ---- Real API Section ----
async function apiFetchActiveYears() {
  try {
    const token = localStorage.getItem('adminAuthToken');
    const response = await fetch(`${API_BASE_URL}/admin/documents/years/`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch years');
    }
    
    const data = await response.json();
    const allYears = data.data || [];
    
    // Filter only active years and sort by year descending
    const activeYears = allYears
      .filter(year => year.is_active)
      .sort((a, b) => b.year - a.year);
    
    return activeYears;
  } catch (error) {
    console.error('Failed to fetch active years:', error);
    return [];
  }
}

async function apiFetchActiveCategories() {
  try {
    const token = localStorage.getItem('adminAuthToken');
    const response = await fetch(`${API_BASE_URL}/admin/documents/categories/`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch categories');
    }
    
    const data = await response.json();
    const allCategories = data.data || [];
    
    // Filter only active categories and sort by name
    const activeCategories = allCategories
      .filter(category => category.is_active)
      .sort((a, b) => a.name.localeCompare(b.name));
    
    return activeCategories;
  } catch (error) {
    console.error('Failed to fetch active categories:', error);
    return [];
  }
}

async function apiFetchCategoryFiles() {
  try {
    // Check if admin is logged in
    const token = localStorage.getItem('adminAuthToken');
    if (!token) {
      throw new Error('Admin not logged in. Please log in again.');
    }
    
    const response = await adminService.getTaxForms();
    return response.data || [];
  } catch (error) {
    console.error('Failed to fetch tax forms:', error);
    
    // If it's an auth error, redirect to login
    if (error.message.includes('401') || error.message.includes('Unauthorized')) {
      localStorage.removeItem('adminAuthToken');
      localStorage.removeItem('adminAuth');
      localStorage.removeItem('adminUser');
      window.location.href = '/admin/login';
      return;
    }
    
    throw new Error('Failed to fetch tax forms');
  }
}

async function apiCreateTaxForm(formData) {
  try {
    const response = await adminService.createTaxForm(formData);
    return response.data;
  } catch (error) {
    console.error('Failed to create tax form:', error);
    throw new Error(error.message || 'Validation failed');
  }
}

async function apiUpdateTaxForm(formId, formData) {
  try {
    const response = await adminService.updateTaxForm(formId, formData);
    return response.data;
  } catch (error) {
    console.error('Failed to update tax form:', error);
    throw new Error(error.message || 'Failed to update tax form');
  }
}

async function apiDeleteTaxForm(formId) {
  try {
    await adminService.deleteTaxForm(formId);
  return true;
  } catch (error) {
    console.error('Failed to delete tax form:', error);
    throw new Error(error.message || 'Failed to delete tax form');
  }
}

// ---- End API Section ----

export default function CategoryFiles() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeYears, setActiveYears] = useState([]);
  const [activeCategories, setActiveCategories] = useState([]);

  // Pagination
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Dialog states
  const [editRow, setEditRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [addDialog, setAddDialog] = useState(false);

  // Success and Error
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // --- Forms
  const initialFields = { 
    title: "", 
    description: "", 
    form_type: "information_form", 
    category: "", 
    year: "", 
    is_public: false, 
    is_active: true, 
    attachment: "" 
  };
  const [editVals, setEditVals] = useState(initialFields);
  const [editErrs, setEditErrs] = useState({});
  const [editTouched, setEditTouched] = useState({});
  const [addVals, setAddVals] = useState(initialFields);
  const [addErrs, setAddErrs] = useState({});
  const [addTouched, setAddTouched] = useState({});
  const editFileRef = useRef();
  const addFileRef = useRef();

  // Fetch on mount
  useEffect(() => {
    async function load() {
      setLoading(true);
      
      // Check if admin is logged in
      const token = localStorage.getItem('adminAuthToken');
      if (!token) {
        setErrorMsg('Admin not logged in. Please log in again.');
        setLoading(false);
        return;
      }
      
      setIsAuthenticated(true);
      
      // Fetch category files, active years, and active categories
      const [categoryFiles, years, categories] = await Promise.all([
        apiFetchCategoryFiles(),
        apiFetchActiveYears(),
        apiFetchActiveCategories()
      ]);
      
      setData(categoryFiles);
      setActiveYears(years);
      setActiveCategories(categories);
      setLoading(false);
    }
    load();
  }, []);

  // --- Columns
  const columns = useMemo(() => [
    {
      name: "S.No",
      width: "60px",
      cell: (_row, idx) => (currentPage - 1) * perPage + idx + 1,
      style: { fontWeight: 600, color: "#4338ca", textAlign: "center", whiteSpace: "nowrap" },
      sortable: false,
    },
    {
      name: "Category",
      selector: row => row.category_name,
      sortable: true,
      width: "130px",
      style: { fontWeight: 500, whiteSpace: "nowrap" },
      cell: row => <span>{row.category_name || 'N/A'}</span>
    },
    {
      name: "Year",
      selector: row => row.year_display,
      sortable: true,
      width: "85px",
      style: { fontWeight: 600, color: "#112", whiteSpace: "nowrap", textAlign: "center" },
      cell: row => <span>{row.year_display || 'N/A'}</span>
    },
    {
      name: "Title",
      selector: row => row.title,
      sortable: true,
      width: "160px",
      cell: row => <span style={{ whiteSpace: "nowrap" }}>{row.title || 'N/A'}</span>,
    },
    {
      name: "Form Type",
      selector: row => row.form_type,
      sortable: true,
      width: "120px",
      cell: row => (
        <span style={{
          color: "#059669",
          fontSize: "12px",
          fontWeight: 500,
          textTransform: "capitalize"
        }}>
          {row.form_type?.replace('_', ' ') || 'N/A'}
        </span>
      ),
      style: { textAlign: "center" }
    },
    {
      name: "Status",
      selector: row => row.is_active,
      width: "115px",
      style: { textAlign: "center", whiteSpace: "nowrap" },
      cell: row => (
        <span style={{
          borderRadius: 14, padding: "5px 15px", fontWeight: 700, color: "#fff", fontSize: 14,
          background: row.is_active ? "#21bb55" : "#dc2626",
          boxShadow: row.is_active ? "0 2px 8px #21bb551a" : "0 2px 9px #dc262611",
          letterSpacing: ".02em",
          display: "inline-block",
          minWidth: 70,
          userSelect: "none"
        }}>
          {row.is_active ? "Active" : "Inactive"}
        </span>
      ),
      sortable: false,
    },
    {
      name: "Actions",
      width: "170px",
      style: { whiteSpace: "nowrap" },
      cell: row => (
        <div style={{
          display: "flex", justifyContent: "center", gap: 8, whiteSpace: "nowrap"
        }}>
          <button onClick={() => openEdit(row)} title="Edit" style={iconBtn('#3193eb')}>
            <AiOutlineEdit size={18} />
          </button>
          <button onClick={() => openDelete(row)} title="Delete" style={iconBtn('#dc2626')}>
            <AiOutlineDelete size={18} />
          </button>
          <button onClick={() => handleChangeStatus(row)} title="Change status" style={iconBtn('#2979f2')}>
            <AiOutlineSync size={18} />
          </button>
        </div>
      ),
      ignoreRowClick: true,
      sortable: false,
    },
    {
      name: "Download",
      width: "120px",
      cell: row =>
        <button
          onClick={() => handleDownloadAttachment(row)}
          style={{
            background: "#ecf6fe",
            color: "#1873ce",
            border: "none",
            borderRadius: 8,
            padding: "6px 12px",
            fontWeight: 600,
            fontSize: 13,
            display: "flex", alignItems: "center", gap: 6, cursor: "pointer",
            minWidth: 70
          }}
          title={`Download ${row.title}`}
        >
          <AiOutlineCloudDownload size={16} />
          Download
        </button>,
      ignoreRowClick: true,
      sortable: false,
      style: { textAlign: "center" }
    }
  ], [currentPage, perPage]);

  // --- Handlers: Pagination
  function handlePageChange(page) { setCurrentPage(page); }
  function handlePerRowsChange(newPerPage, page) {
    setPerPage(newPerPage);
    setCurrentPage(page);
  }

  // --- Download Attachment
  async function handleDownloadAttachment(row) {
    try {
      const token = localStorage.getItem('adminAuthToken');
      
      // Use the correct API endpoint for downloading tax forms
      const response = await fetch(`${API_BASE_URL}/documents/tax-forms/${row.id}/download/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('File not found');
        } else if (response.status === 403) {
          throw new Error('Permission denied');
        } else {
          throw new Error(`Download failed: ${response.status} ${response.statusText}`);
        }
      }
      
      // Get the filename from the Content-Disposition header or use a default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'tax-form.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log(`Downloaded: ${filename}`);
      
    } catch (error) {
      console.error('Download error:', error);
      alert('Download failed: ' + error.message);
    }
  }

  // --- Edit logic
  function openEdit(row) {
    setEditRow(row);
    
    // Map the row data to form values
    // The row data comes from the API with these field names
    // Ensure category_id and year_id are converted to strings (UUIDs)
    const formValues = {
      title: row.title || "",
      description: row.description || "",
      form_type: row.form_type || "information_form",
      category: row.category_id ? String(row.category_id) : "",
      year: row.year_id ? String(row.year_id) : "",
      is_public: row.is_public || false,
      is_active: row.is_active !== false,
      attachment: ""
    };
    
    setEditVals(formValues);
    
    // Mark all fields as touched so validation errors show immediately
    setEditTouched({
      title: true,
      description: true,
      form_type: true,
      category: true,
      year: true,
      is_public: true,
      is_active: true,
      attachment: true
    });
    
    setEditErrs({});
    if (editFileRef.current) editFileRef.current.value = "";
  }
  function validateEdit(vals) {
    const errs = {};
    if (!vals.title.trim()) errs.title = "Title required";
    if (!vals.category) errs.category = "Category required";
    if (!vals.year) errs.year = "Year required";
    if (vals.attachment && !/\.(pdf|docx|txt)$/i.test(vals.attachment.name || "")) {
      errs.attachment = "Only PDF, DOCX, and TXT files are allowed";
    }
    return errs;
  }
  function handleEditInput(e) {
    const { name, value, files } = e.target;
    if (name === "attachment") {
      setEditVals(v => ({ ...v, attachment: files[0] }));
      setEditTouched(t => ({ ...t, attachment: true }));
      setEditErrs(validateEdit({ ...editVals, attachment: files[0] }));
    } else {
      setEditVals(v => ({ ...v, [name]: value }));
      setEditTouched(t => ({ ...t, [name]: true }));
      setEditErrs(validateEdit({ ...editVals, [name]: value }));
    }
  }
  async function handleSaveEdit(e) {
    e.preventDefault();
    const errs = validateEdit(editVals);
    setEditErrs(errs);
    setEditTouched({ title:true, category:true, year:true, attachment:true });
    if (Object.keys(errs).length > 0) return;

    setLoading(true);
    try {
      // Update tax form via API
      const formData = new FormData();
      formData.append('title', editVals.title);
      formData.append('description', editVals.description || '');
      formData.append('form_type', editVals.form_type);
      // Ensure category and year are sent as UUID strings, not numbers
      formData.append('category', String(editVals.category || ''));
      formData.append('year', String(editVals.year || ''));
      formData.append('is_public', editVals.is_public || false);
      formData.append('is_active', editVals.is_active !== false);
      
      if (editVals.attachment) {
        formData.append('file', editVals.attachment);
        // original_filename will be set automatically by the backend from file.name
        console.log('File attached:', editVals.attachment.name, editVals.attachment.size);
      }
      
      await apiUpdateTaxForm(editRow.id, formData);
      
      // Refresh category files, years, and categories
      const [categoryFiles, years, categories] = await Promise.all([
        apiFetchCategoryFiles(),
        apiFetchActiveYears(),
        apiFetchActiveCategories()
      ]);
      
      setData(categoryFiles);
      setActiveYears(years);
      setActiveCategories(categories);
      setSuccessMsg("Tax form updated successfully!");
    } catch (error) {
      console.error('Update error:', error);
      setErrorMsg("Failed to update tax form: " + error.message);
    }
    setEditRow(null);
    setLoading(false);
    setSuccessMsg("Saved successfully!");
  }

  // --- Add logic
  function openAdd() {
    setAddDialog(true);
    setAddVals(initialFields);
    setAddErrs({});
    setAddTouched({});
    if (addFileRef.current) addFileRef.current.value = "";
  }
  function validateAdd(vals) {
    const errs = {};
    if (!vals.title.trim()) errs.title = "Title required";
    if (!vals.category) errs.category = "Category required";
    if (!vals.year) errs.year = "Year required";
    if (!vals.attachment) errs.attachment = "File required";
    else if (!/\.(pdf|docx|txt)$/i.test(vals.attachment.name)) {
      errs.attachment = "Only PDF, DOCX, and TXT files are allowed";
    }
    return errs;
  }
  function handleAddInput(e) {
    const { name, value, files } = e.target;
    if (name === "attachment") {
      setAddVals(v => ({ ...v, attachment: files[0] }));
      setAddTouched(t => ({ ...t, attachment: true }));
      setAddErrs(validateAdd({ ...addVals, attachment: files[0] }));
    } else {
      setAddVals(v => ({ ...v, [name]: value }));
      setAddTouched(t => ({ ...t, [name]: true }));
      setAddErrs(validateAdd({ ...addVals, [name]: value }));
    }
  }
  async function handleSaveAdd(e) {
    e.preventDefault();
    const errs = validateAdd(addVals);
    setAddErrs(errs);
    setAddTouched({ title:true, category:true, year:true, attachment:true });
    if (Object.keys(errs).length > 0) return;

    setLoading(true);
    try {
      // Create new tax form via API
      const formData = new FormData();
      formData.append('title', addVals.title);
      formData.append('description', addVals.description || '');
      formData.append('form_type', addVals.form_type);
      formData.append('category', addVals.category);
      formData.append('year', addVals.year);
      formData.append('is_public', addVals.is_public || false);
      formData.append('is_active', true);
      
      if (addVals.attachment) {
        formData.append('file', addVals.attachment);
        // original_filename will be set automatically by the backend from file.name
        console.log('File attached:', addVals.attachment.name, addVals.attachment.size);
      } else {
        console.error('No file attached!');
        throw new Error('File is required');
      }
      
      await apiCreateTaxForm(formData);
      
      // Refresh category files, years, and categories
      const [categoryFiles, years, categories] = await Promise.all([
        apiFetchCategoryFiles(),
        apiFetchActiveYears(),
        apiFetchActiveCategories()
      ]);
      
      setData(categoryFiles);
      setActiveYears(years);
      setActiveCategories(categories);
      setSuccessMsg("Tax form created successfully!");
    } catch (error) {
      console.error('Create error:', error);
      setErrorMsg("Failed to create tax form: " + error.message);
    }
    setAddDialog(false);
    setLoading(false);
    setSuccessMsg("Saved successfully!");
  }

  // --- Delete
  function openDelete(row) { setDeleteRow(row); }
  async function handleDeleteYes() {
    setLoading(true);
    try {
      await apiDeleteTaxForm(deleteRow.id);
      
      // Refresh category files, years, and categories
      const [categoryFiles, years, categories] = await Promise.all([
        apiFetchCategoryFiles(),
        apiFetchActiveYears(),
        apiFetchActiveCategories()
      ]);
      
      setData(categoryFiles);
      setActiveYears(years);
      setActiveCategories(categories);
      setSuccessMsg("Tax form deleted successfully!");
    } catch (error) {
      console.error('Delete error:', error);
      setErrorMsg("Failed to delete tax form: " + error.message);
    }
    setDeleteRow(null);
    setLoading(false);
  }

  // --- Actions
  async function handleChangeStatus(row) {
    setLoading(true);
    try {
      // Toggle the is_active status using the dedicated toggle endpoint
      const response = await adminService.toggleTaxFormStatus(row.id);
      
      // Refresh category files, years, and categories
      const [categoryFiles, years, categories] = await Promise.all([
        apiFetchCategoryFiles(),
        apiFetchActiveYears(),
        apiFetchActiveCategories()
      ]);
      
      setData(categoryFiles);
      setActiveYears(years);
      setActiveCategories(categories);
      
      // Show success message with the new status
      const newStatus = !row.is_active;
      setSuccessMsg(`Tax form ${newStatus ? 'activated' : 'deactivated'} successfully!`);
    } catch (error) {
      console.error('Status change error:', error);
      setErrorMsg("Failed to change status: " + error.message);
    }
    setLoading(false);
  }

  function closeSuccess() { setSuccessMsg(""); }

  // --- Table styles
  const customStyles = {
    table: { style: { width: "100%" } },
    rows: { style: { fontSize: 15, background: "#f8fafc", whiteSpace: "nowrap" } },
    headRow: { style: { background: "#eceffd", color: "#433ca", fontWeight: 700, fontSize: 15 } },
    headCells: { style: { borderBottom: "1.6px solid #a5a6ff", padding: "8px 10px" } },
    cells: { style: { padding: "8px 10px" } },
    pagination: { style: { borderTop: "1px solid #aab6fe", color: "#433ca", fontWeight: 600 } }
  };

  // --- Helpers
  function iconBtn(bg) {
    return {
      background: bg,
      color: "#fff",
      border: "none",
      borderRadius: 7,
      padding: 6,
      margin: 0,
      cursor: "pointer",
      boxShadow: bg === "#2979f2" ? "0 2px 5px #2979f222" : undefined,
      width: 32,
      height: 32,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      transition: "background-color 0.2s"
    };
  }

  // Show authentication error if not logged in
  if (!isAuthenticated && !loading) {
    return (
      <section style={{ padding: 20, textAlign: "center" }}>
        <div style={{ 
          background: "#fef2f2", 
          border: "1px solid #fecaca", 
          borderRadius: 8, 
          padding: 20, 
          color: "#dc2626",
          maxWidth: 500,
          margin: "50px auto"
        }}>
          <h2 style={{ margin: "0 0 10px 0" }}>Authentication Required</h2>
          <p style={{ margin: "0 0 20px 0" }}>Please log in as an administrator to access this page.</p>
          <button 
            onClick={() => window.location.href = '/admin/login'}
            style={{
              background: "#dc2626",
              color: "#fff",
              border: "none",
              padding: "10px 20px",
              borderRadius: 6,
              cursor: "pointer",
              fontSize: 16
            }}
          >
            Go to Login
          </button>
        </div>
      </section>
    );
  }

  return (
    <section style={{ //maxWidth: 1300, margin: "0 auto", 
    padding: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", marginBottom: 20 }}>
        <h1 style={{ fontWeight: 900, fontSize: 27, color: "#216096", display: "flex", alignItems: "center", gap: 11 }}>
          <AiOutlineFileWord size={30} color="#216096" />
          Category Files Management
        </h1>
        <button
          onClick={openAdd}
          style={{
            background: "linear-gradient(90deg,#217eed,#49dfa9 100%)",
            color: "#fff",
            borderRadius: 9,
            fontWeight: 700,
            fontSize: 16,
            padding: "8px 26px",
            border: "none",
            display: "flex", alignItems: "center", gap: 8,
            cursor: "pointer", boxShadow: "0 2px 10px #49dfa955"
          }}
        >
          <AiOutlinePlus size={20} />
          Add Category
        </button>
      </div>
      <DataTable
        columns={columns}
        data={data}
        highlightOnHover
        striped
        pagination
        paginationPerPage={perPage}
        paginationRowsPerPageOptions={[10, 20, 30, 40, 50]}
        onChangeRowsPerPage={handlePerRowsChange}
        onChangePage={handlePageChange}
        paginationDefaultPage={currentPage}
        progressPending={loading}
        noHeader
        responsive
        persistTableHead
        customStyles={customStyles}
      />

      {/* --- ADD DIALOG --- */}
      {addDialog && (
        <Modal onClose={() => setAddDialog(false)} title="Add Category">
          <form onSubmit={handleSaveAdd} className="responsive-form">
            <div className="form-row">
              <div className="form-group">
                <FormField label="Title" name="title" value={addVals.title} onChange={handleAddInput}
                  touched={addTouched.title} error={addErrs.title} />
              </div>
              <div className="form-group">
                <label style={{ display: "block", marginBottom: 6, fontWeight: 600, color: "#333" }}>Form Type</label>
                <select
                  name="form_type"
                  value={addVals.form_type}
                  onChange={handleAddInput}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #ddd",
                    borderRadius: 6,
                    fontSize: 14
                  }}
                >
                  <option value="information_form">Information Form</option>
                  <option value="draft_return">Draft Tax Return</option>
                  <option value="final_return">Final Tax Return</option>
                  <option value="template">Template</option>
                  <option value="instruction">Instructions</option>
                </select>
              </div>
            </div>
            
            <div className="form-group full-width">
              <FormField label="Description" name="description" value={addVals.description} onChange={handleAddInput}
                touched={addTouched.description} error={addErrs.description} />
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label style={{ display: "block", marginBottom: 6, fontWeight: 600, color: "#333" }}>Category</label>
                <select
                  name="category"
                  value={addVals.category}
                  onChange={handleAddInput}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #ddd",
                    borderRadius: 6,
                    fontSize: 14
                  }}
                >
                  <option value="">Select Category</option>
                  {activeCategories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                {addTouched.category && addErrs.category && <div style={{ color: "#dc2626", fontSize: "12px", marginTop: "4px" }}>{addErrs.category}</div>}
              </div>
              <div className="form-group">
                <label style={{ display: "block", marginBottom: 6, fontWeight: 600, color: "#333" }}>Year</label>
                <select
                  name="year"
                  value={addVals.year}
                  onChange={handleAddInput}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #ddd",
                    borderRadius: 6,
                    fontSize: 14
                  }}
                >
                  <option value="">Select Year</option>
                  {activeYears.map(year => (
                    <option key={year.id} value={year.id}>
                      {year.year}
                    </option>
                  ))}
                </select>
                {addTouched.year && addErrs.year && <div style={{ color: "#dc2626", fontSize: "12px", marginTop: "4px" }}>{addErrs.year}</div>}
              </div>
            </div>
            
            <div className="form-group full-width">
              <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <input
                  type="checkbox"
                  name="is_public"
                  checked={addVals.is_public}
                  onChange={(e) => setAddVals(v => ({ ...v, is_public: e.target.checked }))}
                />
                <span style={{ fontWeight: 600, color: "#333" }}>Public (visible to users)</span>
              </label>
            </div>
            
            <div className="form-group full-width">
              <label style={labelStyle}>File (PDF, DOCX, TXT) <span style={{ color: "#dd362b" }}>*</span></label>
              <input
                ref={addFileRef}
                type="file"
                name="attachment"
                accept=".pdf,.docx,.txt"
                onChange={handleAddInput}
                style={{ ...inputStyle, padding: 6, fontSize: 15, lineHeight: 1.1 }}
                required
              />
              <span style={{ color: "#2c62f5", fontSize: 14 }}>{addVals.attachment && addVals.attachment.name}</span>
              {addTouched.attachment && addErrs.attachment && <div style={errStyle}>{addErrs.attachment}</div>}
            </div>
            
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 15, marginTop: 6 }}>
              <button type="button" onClick={() => setAddDialog(false)} style={modalBtn("#cad2ff", "#234b8a")}>Cancel</button>
              <button
                type="submit"
                style={{
                  ...modalBtn("#216096", "#fff"),
                  ...(Object.keys(validateAdd(addVals)).length > 0 ? {
                    background: "#ccc",
                    color: "#666",
                    cursor: "not-allowed",
                    opacity: 0.6
                  } : {})
                }}
                disabled={Object.keys(validateAdd(addVals)).length > 0}
              >Add</button>
            </div>
          </form>
        </Modal>
      )}

      {/* --- EDIT DIALOG --- */}
      {editRow && (
        <Modal onClose={() => setEditRow(null)} title="Edit Category">
          <form onSubmit={handleSaveEdit} className="responsive-form">
            <div className="form-row">
              <div className="form-group">
                <FormField label="Title" name="title" value={editVals.title} onChange={handleEditInput}
                  touched={editTouched.title} error={editErrs.title} />
              </div>
              <div className="form-group">
                <label style={{ display: "block", marginBottom: 6, fontWeight: 600, color: "#333" }}>Form Type</label>
                <select
                  name="form_type"
                  value={editVals.form_type}
                  onChange={handleEditInput}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #ddd",
                    borderRadius: 6,
                    fontSize: 14
                  }}
                >
                  <option value="information_form">Information Form</option>
                  <option value="draft_return">Draft Tax Return</option>
                  <option value="final_return">Final Tax Return</option>
                  <option value="template">Template</option>
                  <option value="instruction">Instructions</option>
                </select>
              </div>
            </div>
            
            <div className="form-group full-width">
              <FormField label="Description" name="description" value={editVals.description} onChange={handleEditInput}
                touched={editTouched.description} error={editErrs.description} />
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label style={{ display: "block", marginBottom: 6, fontWeight: 600, color: "#333" }}>Category</label>
                <select
                  name="category"
                  value={editVals.category}
                  onChange={handleEditInput}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #ddd",
                    borderRadius: 6,
                    fontSize: 14
                  }}
                >
                  <option value="">Select Category</option>
                  {activeCategories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                {editTouched.category && editErrs.category && <div style={{ color: "#dc2626", fontSize: "12px", marginTop: "4px" }}>{editErrs.category}</div>}
              </div>
              <div className="form-group">
                <label style={{ display: "block", marginBottom: 6, fontWeight: 600, color: "#333" }}>Year</label>
                <select
                  name="year"
                  value={editVals.year}
                  onChange={handleEditInput}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #ddd",
                    borderRadius: 6,
                    fontSize: 14
                  }}
                >
                  <option value="">Select Year</option>
                  {activeYears.map(year => (
                    <option key={year.id} value={year.id}>
                      {year.year}
                    </option>
                  ))}
                </select>
                {editTouched.year && editErrs.year && <div style={{ color: "#dc2626", fontSize: "12px", marginTop: "4px" }}>{editErrs.year}</div>}
              </div>
            </div>
            
            <div className="form-group full-width">
              <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <input
                  type="checkbox"
                  name="is_public"
                  checked={editVals.is_public}
                  onChange={(e) => setEditVals(v => ({ ...v, is_public: e.target.checked }))}
                />
                <span style={{ fontWeight: 600, color: "#333" }}>Public (visible to users)</span>
              </label>
            </div>
            
            <div className="form-group full-width">
              <label style={labelStyle}>File (PDF, DOCX, TXT)</label>
              {editRow && editRow.filename && (
                <div style={{ 
                  background: "#f0f9ff", 
                  border: "1px solid #bae6fd", 
                  borderRadius: 6, 
                  padding: "8px 12px", 
                  marginBottom: 8,
                  fontSize: 14,
                  color: "#0369a1"
                }}>
                  <strong>Current file:</strong> {editRow.filename}
                </div>
              )}
              <input
                ref={editFileRef}
                type="file"
                name="attachment"
                accept=".pdf,.docx,.txt"
                onChange={handleEditInput}
                style={{ ...inputStyle, padding: 6, fontSize: 15, lineHeight: 1.1 }}
              />
              <span style={{ color: "#2c62f5", fontSize: 14 }}>
                {editVals.attachment && editVals.attachment.name ? `New file: ${editVals.attachment.name}` : "No new file selected"}
              </span>
              {editTouched.attachment && editErrs.attachment && <div style={errStyle}>{editErrs.attachment}</div>}
            </div>
            
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 15, marginTop: 6 }}>
              <button type="button" onClick={() => setEditRow(null)} style={modalBtn("#cad2ff", "#234b8a")}>Cancel</button>
              <button
                type="submit"
                style={{
                  ...modalBtn("#216096", "#fff"),
                  ...(Object.keys(validateEdit(editVals)).length > 0 ? {
                    background: "#ccc",
                    color: "#666",
                    cursor: "not-allowed",
                    opacity: 0.6
                  } : {})
                }}
                disabled={Object.keys(validateEdit(editVals)).length > 0}
              >Save</button>
            </div>
          </form>
        </Modal>
      )}

      {/* --- DELETE DIALOG --- */}
      {deleteRow && (
        <Modal onClose={() => setDeleteRow(null)} title="Delete Category">
          <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 18 }}>
            Are you sure you want to delete <b>{deleteRow.filename}</b>?
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
            <button onClick={() => setDeleteRow(null)} style={modalBtn("#cad2ff", "#234b8a")}>Cancel</button>
            <button onClick={handleDeleteYes} style={modalBtn("#dc2626", "#fff")}>Delete</button>
          </div>
        </Modal>
      )}

      {/* --- SUCCESS MODAL --- */}
      {successMsg && (
        <SuccessModal message={successMsg} onClose={closeSuccess} />
      )}

      {/* --- ERROR MODAL --- */}
      {errorMsg && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 10000
        }}>
          <div style={{
            background: "#fff",
            padding: "20px",
            borderRadius: "8px",
            maxWidth: "400px",
            textAlign: "center"
          }}>
            <div style={{ color: "#dc2626", fontSize: "18px", fontWeight: "600", marginBottom: "10px" }}>
              Error
            </div>
            <div style={{ color: "#333", marginBottom: "20px" }}>
              {errorMsg}
            </div>
            <button
              onClick={() => setErrorMsg("")}
              style={{
                background: "#dc2626",
                color: "#fff",
                border: "none",
                padding: "8px 16px",
                borderRadius: "4px",
                cursor: "pointer"
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
      {/* Responsive fixes and form styles */}
      <style>
        {`
          /* Responsive form styles */
          .responsive-form {
            display: flex;
            flex-direction: column;
            gap: 20px;
            min-width: 320px;
          }
          
          .form-row {
            display: flex;
            gap: 20px;
            flex-wrap: wrap;
          }
          
          .form-group {
            flex: 1;
            min-width: 200px;
          }
          
          .form-group.full-width {
            flex: 1 1 100%;
            min-width: 100%;
          }
          
          /* Mobile styles */
          @media (max-width: 768px) {
            .form-row {
              flex-direction: column;
              gap: 15px;
            }
            
            .form-group {
              min-width: 100%;
            }
            
            .responsive-form {
              gap: 15px;
            }
          }
          
          /* Small mobile styles */
          @media (max-width: 480px) {
            .responsive-form {
              gap: 12px;
            }
            
            .form-row {
              gap: 12px;
            }
          }
          
          /* Table responsive fixes */
          @media (max-width: 850px) {
            .rdt_TableCol, .rdt_TableCell {
              padding-left: 5px !important;
              padding-right: 5px !important;
              font-size: 13px;
            }
          }
        `}
      </style>
    </section>
  );
}

// ---- Helpers ----

function FormField({ label, name, value, onChange, touched, error, inputProps }) {
  return (
    <div>
      <label style={labelStyle}>
        {label} <span style={{ color: "#dd362b" }}>*</span>
      </label>
      <input
        name={name}
        type="text"
        value={value}
        onChange={onChange}
        style={inputStyle}
        {...(inputProps || {})}
        required
      />
      {touched && error && (
        <span style={errStyle}>{error}</span>
      )}
    </div>
  );
}
function iconBtn(bg) {
  return {
    background: bg,
    color: "#fff",
    border: "none",
    borderRadius: 7,
    padding: 6,
    margin: 0,
    cursor: "pointer",
    boxShadow: bg === "#2979f2" ? "0 2px 5px #2979f222" : undefined,
    width: 32,
    height: 32,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    transition: "background-color 0.2s"
  };
}
function modalBtn(bg, color, px=16, py=8) {
  return {
    padding: `${py}px ${px}px`,
    background: bg,
    color: color,
    border: "none",
    borderRadius: 7,
    fontWeight: 700,
    fontSize: 15,
    cursor: "pointer",
    outline: "none",
  };
}
const labelStyle = { color: "#216096", fontWeight: 700, fontSize: 15, marginBottom: 7, display: "block" };
const inputStyle = {
  padding: 11, fontSize: 16, borderRadius: 7, border: "1.6px solid #aec0fa",
  outline: "none", width: "100%", fontWeight: 600, color: "#243567", letterSpacing: ".04em", background: "#f6f9ff"
};
const errStyle = { color: "#dc2626", fontWeight: 600, fontSize: 13, marginTop: 2 };

function Modal({ onClose, title, children }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="modalTitle"
      style={{
        position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: "rgba(0,0,0,0.18)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10001,
        padding: "20px",
        overflow: "auto"
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#f9fcfe",
          borderRadius: 15,
          position: "relative",
          padding: "37px 36px 24px 36px",
          minWidth: "320px",
          maxWidth: "90vw",
          width: "auto",
          maxHeight: "90vh",
          overflow: "auto",
          boxShadow: "0 10px 40px #a5b4fc3a",
        }}
      >
        <h3 id="modalTitle" style={{ fontWeight: 700, color: "#216096", margin: 0, marginBottom: 22, fontSize: 22 }}>
          {title}
        </h3>
        {children}
        <button
          aria-label="Close"
          onClick={onClose}
          style={{
            position: "absolute", top: 14, right: 15, background: "transparent",
            border: "none", fontSize: 22, cursor: "pointer", color: "#666", userSelect: "none",
          }}
        >
          <AiOutlineClose />
        </button>
      </div>
    </div>
  );
}

function SuccessModal({ message, onClose }) {
  return (
    <div
      role="alertdialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: "rgba(0,0,0,0.13)", zIndex: 10002,
        display: "flex", justifyContent: "center", alignItems: "center"
      }}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fafff9",
          padding: "36px 34px 26px 34px",
          borderRadius: 16,
          textAlign: "center",
          boxShadow: "0 8px 36px #53ec8860",
          color: "#11602c"
        }}>
        <AiOutlineCheckCircle
          size={62}
          color="#17bb5f"
          style={{ marginBottom: 13, animation: "pop-tick .33s cubic-bezier(.19,.7,.68,1.38)" }}
        />
        <div style={{ fontWeight: 800, fontSize: 21, marginBottom: 2 }}>{message}</div>
        <button onClick={onClose} style={modalBtn("#17bb5f", "#fff", 17, 10)}>Close</button>
        <style>{`
          @keyframes pop-tick {
            0% { opacity: 0; transform: scale(0.46);}
            67% { opacity: 1; transform: scale(1.14);}
            100% { opacity: 1; transform: scale(1);}
          }
        `}</style>
      </div>
    </div>
  );
}
